import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Sparkles, Lightbulb, ChevronRight, ChevronDown, Loader2,
  RefreshCw, Lock, TrendingDown, Shield, Users, CheckCircle,
  Target, ClipboardList, ArrowUpRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useUser } from '@/lib/UserContext';
import { cleanMarkdown } from '@/lib/formatAIText';
import { ActionPlanPanel } from '@/components/AutoRecommendations';
import { HelpBubble } from '@/components/HelpBubble';

const REC_ICONS = {
  cost_savings: TrendingDown,
  risk_mitigation: Shield,
  consolidation: Users,
  compliance: CheckCircle,
  strategic: Lightbulb,
};

const REC_COLORS = {
  cost_savings: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800',
  risk_mitigation: 'text-amber-600 bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800',
  consolidation: 'text-blue-600 bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800',
  compliance: 'text-violet-600 bg-violet-50 dark:bg-violet-950/40 border-violet-200 dark:border-violet-800',
  strategic: 'text-sky-600 bg-sky-50 dark:bg-sky-950/40 border-sky-200 dark:border-sky-800',
};

const PRIORITY_DOT = { high: 'bg-red-500', medium: 'bg-amber-500', low: 'bg-emerald-500' };

export function AIPanel({ page, filters, insightsMode = 'page' }) {
  const user = useUser();
  const isSuperAdmin = (user?.roles || []).includes('super_admin');
  const caps = user?.ai_capabilities || {};
  const hasInsightsCap = isSuperAdmin || caps.ai_page_insights !== false;
  const hasDigestCap = isSuperAdmin || caps.ai_dashboard_digest !== false;
  const hasRecsCap = isSuperAdmin || caps.ai_recommendations !== false;
  // Org-level surface scope: off → hidden everywhere, dashboard → Dashboard only, all → every page
  const recsScope = isSuperAdmin ? 'all' : (user?.ai_recommendations_scope || 'all');
  const scopeAllowsThisPage = recsScope === 'all' || (recsScope === 'dashboard' && page === 'dashboard');
  const scopeHidesRecs = recsScope === 'off' || !scopeAllowsThisPage;
  const hasRecommendations = (isSuperAdmin || (user?.subscribed_features || []).includes('ai_recommendations')) && hasRecsCap && !scopeHidesRecs;
  const hasUserAccess = user?.ai_access !== false;
  const isDigest = insightsMode === 'digest';
  const insightsAllowed = hasUserAccess && (isDigest ? hasDigestCap : hasInsightsCap);

  const [insights, setInsights] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [recsAudit, setRecsAudit] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [recsOpen, setRecsOpen] = useState(false);
  const [insightsError, setInsightsError] = useState(null);
  const [recsError, setRecsError] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const lastFiltersRef = useRef(null);
  const fetchIdRef = useRef(0);

  const cacheKey = `ai_cache_${user?.organization_id || 'anon'}_${page}${isDigest ? '_digest' : ''}`;

  // Load from cache on mount
  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
      if (cached) {
        setInsights(cached.insights || []);
        setRecommendations(cached.recommendations || []);
        setPriorities(cached.priorities || []);
        if (isSuperAdmin) setRecsAudit(cached.recsAudit || null);
        // Check if filters changed since last cache
        const currentFilterStr = JSON.stringify(filters || {});
        if (cached.filterStr && cached.filterStr !== currentFilterStr) {
          setIsStale(true);
        }
      }
    } catch {}
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Persist digest priorities into the existing cache envelope
  useEffect(() => {
    if (!isDigest || !priorities.length) return;
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
      if (cached) {
        cached.priorities = priorities;
        localStorage.setItem(cacheKey, JSON.stringify(cached));
      }
    } catch {}
  }, [priorities]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Detect filter changes → mark stale
  useEffect(() => {
    const filterStr = JSON.stringify(filters || {});
    if (lastFiltersRef.current !== null && filterStr !== lastFiltersRef.current) {
      setIsStale(true);
    }
    lastFiltersRef.current = filterStr;
  }, [filters]);

  const saveToCache = (newInsights, newRecs, newAudit) => {
    try {
      localStorage.setItem(cacheKey, JSON.stringify({
        insights: newInsights,
        recommendations: newRecs,
        recsAudit: newAudit,
        filterStr: JSON.stringify(filters || {}),
        generatedAt: new Date().toISOString(),
      }));
    } catch {}
  };

  const fetchInsights = useCallback(async (f) => {
    if (!insightsAllowed) return [];
    const id = ++fetchIdRef.current;
    setLoadingInsights(true);
    setInsightsError(null);
    try {
      const endpoint = isDigest ? '/ai/dashboard-digest' : '/ai/page-insights';
      const res = await api.post(endpoint, isDigest ? { filters: f || {} } : { page, filters: f || {} });
      if (id === fetchIdRef.current) {
        const newInsights = isDigest ? (res.data.signals || []) : (res.data.insights || []);
        setInsights(newInsights);
        if (isDigest) setPriorities(res.data.investigation_priorities || []);
        return newInsights;
      }
    } catch {
      if (id === fetchIdRef.current) setInsightsError(isDigest ? 'Unable to generate digest' : 'Unable to generate insights');
    } finally {
      if (id === fetchIdRef.current) setLoadingInsights(false);
    }
    return [];
  }, [page, isDigest, insightsAllowed]);

  const fetchRecommendations = useCallback(async (f) => {
    if (!hasRecommendations || !hasUserAccess) return { recs: [], audit: null };
    setLoadingRecs(true);
    setRecsError(null);
    try {
      const res = await api.post('/ai/recommendations', { page, filters: f || {} });
      const newRecs = res.data.recommendations || [];
      setRecommendations(newRecs);
      let audit = null;
      if (isSuperAdmin) {
        audit = {
          data_summary: res.data.data_summary || null,
          cache: res.data._cache || 'unknown',
          filters_received: res.data.filters_received || null,
          generated_at: new Date().toISOString(),
        };
        setRecsAudit(audit);
      }
      return { recs: newRecs, audit };
    } catch (err) {
      if (err.response?.status === 403) {
        setRecommendations([]);
      } else {
        setRecsError('Unable to generate recommendations');
      }
    } finally {
      setLoadingRecs(false);
    }
    return { recs: [], audit: null };
  }, [page, hasRecommendations, hasUserAccess, isSuperAdmin]);

  const handleRefresh = async () => {
    setIsStale(false);
    const [newInsights, recsResult] = await Promise.all([
      fetchInsights(filters),
      hasRecommendations && hasUserAccess ? fetchRecommendations(filters) : Promise.resolve({ recs: [], audit: null }),
    ]);
    const newRecs = recsResult.recs;
    const newAudit = recsResult.audit;
    saveToCache(newInsights || insights, newRecs || recommendations, newAudit || recsAudit);
  };

  // Auto-fetch only on first load if no cache exists
  // Auto-fetch only on first load if no cache exists
  useEffect(() => {
    if (!insightsAllowed && !(hasRecommendations && hasUserAccess)) return;
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
      if (!cached || !cached.insights?.length) {
        handleRefresh();
      }
    } catch {
      handleRefresh();
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const insightsCount = insights.length;
  const recsCount = recommendations.length;

  return (
    <div className="space-y-3 mt-4" data-testid="ai-panel">
      {/* Stale data banner */}
      {isStale && (insightsCount > 0 || recsCount > 0) && (
        <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20 text-xs" data-testid="ai-stale-banner">
          <span className="text-amber-700 dark:text-amber-400">
            Data or filters have changed since these insights were generated.
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-[10px] gap-1 shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-400"
            onClick={handleRefresh}
            disabled={loadingInsights || loadingRecs}
            data-testid="ai-refresh-stale-btn"
          >
            {(loadingInsights || loadingRecs) ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refresh AI Analysis
          </Button>
        </div>
      )}

      {/* Insights Section — always available */}
      {insightsAllowed && (
      <Collapsible open={insightsOpen} onOpenChange={setInsightsOpen}>
        <Card data-testid="ai-insights-section">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  <CardTitle className="text-sm flex items-center gap-1.5">
                    {isDigest ? 'Cross-Page Signals' : 'AI Insights'}
                    <HelpBubble text="Insights are cached to save cost. If you apply new filters, upload fresh data, or reclassify categories, click Refresh (top-right of this card) to regenerate — otherwise you may be reading stale analysis." />
                  </CardTitle>
                  {loadingInsights && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                  {insightsCount > 0 && !loadingInsights && (
                    <Badge variant="secondary" className="text-[10px]">{insightsCount}</Badge>
                  )}
                </div>
                {insightsOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 pb-3">
              {insightsError ? (
                <p className="text-xs text-muted-foreground text-center py-3">{insightsError}</p>
              ) : insights.length === 0 && !loadingInsights ? (
                <p className="text-xs text-muted-foreground text-center py-3">
                  {isDigest ? 'Click to generate an executive digest across all accessible pages' : 'Click to generate insights from your data'}
                </p>
              ) : isDigest ? (
                <div className="space-y-2">
                  {priorities.length > 0 && (
                    <div className="mb-3 p-3 rounded-md border border-purple-200 bg-purple-50/50 dark:bg-purple-950/20" data-testid="digest-priorities">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[11px] font-semibold text-purple-900 dark:text-purple-200 uppercase tracking-wide">
                          This week's investigation priorities
                        </p>
                        <Link
                          to="/?tab=signals"
                          className="text-[10px] text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 font-medium flex items-center gap-0.5"
                          data-testid="digest-priorities-view-all"
                        >
                          Signal feed <ArrowUpRight className="w-3 h-3" />
                        </Link>
                      </div>
                      <ol className="space-y-2">
                        {priorities.map((p, i) => (
                          <li key={p.id || i} className="flex items-start gap-2 text-xs" data-testid={`digest-priority-${i}`}>
                            <span className="font-bold text-purple-600 dark:text-purple-400 shrink-0 w-4">{i + 1}.</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <Badge variant="outline" className="text-[9px] uppercase">{String(p.signal_type || '').replace('_', ' ')}</Badge>
                                <span className="font-medium">{p.title}</span>
                                {p.value_str && (
                                  <span className="font-semibold text-emerald-700 dark:text-emerald-400">{p.value_str}</span>
                                )}
                              </div>
                              <p className="text-muted-foreground mt-0.5 leading-relaxed">{p.question}</p>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                  {insights.map((signal, i) => (
                    <div key={i} className="p-2.5 rounded-md border bg-muted/20 text-xs hover:bg-muted/40 transition-colors" data-testid={`digest-signal-${i}`}>
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-3.5 h-3.5 text-purple-500 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm">{signal.title}</p>
                            <Badge variant="outline" className="text-[9px]">{signal.page_label}</Badge>
                          </div>
                          <p className="text-muted-foreground mt-1 leading-relaxed">{cleanMarkdown(signal.description || '')}</p>
                        </div>
                        {signal.route && (
                          <Link
                            to={signal.route}
                            className="shrink-0 text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 flex items-center gap-0.5 text-[10px] font-medium"
                            data-testid={`digest-goto-${signal.page}`}
                          >
                            View <ArrowUpRight className="w-3 h-3" />
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {insights.map((insight, i) => (
                    <div key={i} className="p-2.5 rounded-md border bg-muted/20 text-xs" data-testid={`insight-${i}`}>
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-3.5 h-3.5 text-purple-500 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{insight.title || insight.area || `Insight ${i + 1}`}</p>
                          <p className="text-muted-foreground mt-1 leading-relaxed">{cleanMarkdown(insight.description || insight.text || '')}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-end mt-2">
                <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={handleRefresh} disabled={loadingInsights} data-testid="refresh-insights-btn">
                  <RefreshCw className={`w-3 h-3 ${loadingInsights ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
      )}

      {/* Recommendations Section — premium only */}
      {hasRecommendations && hasUserAccess ? (
        <Collapsible open={recsOpen} onOpenChange={setRecsOpen}>
          <Card className="border-l-4 border-l-amber-400" data-testid="ai-recommendations-section">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-500" />
                    <CardTitle className="text-sm flex items-center gap-1.5">
                      AI Recommendations
                      <HelpBubble text="Recommendations are cached per data snapshot to keep AI cost predictable. After changing filters, uploading data, or reclassifying, hit Refresh to see recommendations aligned to the new context." />
                    </CardTitle>
                    {loadingRecs && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                    {recsCount > 0 && !loadingRecs && (
                      <Badge className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400">{recsCount}</Badge>
                    )}
                  </div>
                  {recsOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 pb-3">
                {recsError ? (
                  <p className="text-xs text-muted-foreground text-center py-3">{recsError}</p>
                ) : recommendations.length === 0 && !loadingRecs ? (
                  <p className="text-xs text-muted-foreground text-center py-3">Click to generate actionable recommendations</p>
                ) : (
                  <div className="space-y-2">
                    {recommendations.map((rec, i) => {
                      const Icon = REC_ICONS[rec.category] || Target;
                      const colors = REC_COLORS[rec.category] || REC_COLORS.strategic;
                      return (
                        <div key={i} className={`p-3 rounded-md border ${colors}`} data-testid={`recommendation-${i}`}>
                          <div className="flex items-start gap-2">
                            <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
                              <div className={`w-2 h-2 rounded-full ${PRIORITY_DOT[rec.priority] || PRIORITY_DOT.medium}`} />
                              <Icon className="w-3.5 h-3.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-semibold text-sm">{rec.title}</p>
                                {rec.timeline && (
                                  <Badge variant="outline" className="text-[9px]">{rec.timeline}</Badge>
                                )}
                              </div>
                              <p className="text-xs mt-1 opacity-80 leading-relaxed">{cleanMarkdown(rec.description || '')}</p>
                              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                                {rec.potential_impact && (
                                  <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                                    Impact: {rec.potential_impact}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Badge variant="outline" className="text-[9px] shrink-0">{rec.category?.replace('_', ' ')}</Badge>
                          </div>
                          <ActionPlanPanel rec={rec} filters={filters} />
                          {/* SUPER-ADMIN ONLY: governance-grade audit trail showing the data the AI used */}
                          {isSuperAdmin && recsAudit?.data_summary && (
                            <RecAuditDetails rec={rec} audit={recsAudit} index={i} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="flex justify-end mt-2">
                  <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={handleRefresh} disabled={loadingRecs} data-testid="refresh-recs-btn">
                    <RefreshCw className={`w-3 h-3 ${loadingRecs ? 'animate-spin' : ''}`} /> Refresh
                  </Button>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      ) : scopeHidesRecs ? null : (
        <Card className="opacity-60" data-testid="ai-recommendations-locked">
          <CardHeader className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-muted-foreground" />
                <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">
                  AI Recommendations
                  <HelpBubble text="Recommendations are cached per data snapshot to keep AI cost predictable. After changing filters, uploading data, or reclassifying, hit Refresh to see recommendations aligned to the new context." />
                </CardTitle>
                <Badge variant="outline" className="text-[10px]">Premium</Badge>
              </div>
            </div>
          </CardHeader>
        </Card>
      )}
    </div>
  );
}


/**
 * SUPER-ADMIN ONLY component. Renders a small expander showing the exact data
 * payload the AI used to produce a recommendation. Provides governance-grade
 * transparency so super admins can verify the AI isn't hallucinating and is
 * grounding its calls in real tenant data. NEVER rendered for non-super users
 * — the parent guards visibility with `isSuperAdmin && recsAudit?.data_summary`.
 */
function RecAuditDetails({ rec, audit, index }) {
  const [open, setOpen] = useState(false);
  const ds = audit?.data_summary || {};
  const entries = Object.entries(ds).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (entries.length === 0) return null;

  // Split into short scalar fields (grid) and long text blocks (verbatim <pre>)
  const isLongText = (v) => typeof v === 'string' && (v.length > 80 || v.includes('\n'));
  const scalarEntries = entries.filter(([, v]) => !isLongText(v));
  const textEntries = entries.filter(([, v]) => isLongText(v));

  const fmt = (v) => {
    if (typeof v === 'number') {
      return Math.abs(v) >= 1000 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : String(v);
    }
    if (typeof v === 'boolean') return v ? 'yes' : 'no';
    if (Array.isArray(v)) return v.length > 0 ? `[${v.length} items]` : '[]';
    if (typeof v === 'object') return JSON.stringify(v).slice(0, 80);
    return String(v).slice(0, 80);
  };

  const prettyLabel = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="mt-2 pt-2 border-t border-dashed border-current/20" data-testid={`rec-audit-${index}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-[10px] font-medium opacity-70 hover:opacity-100 flex items-center gap-1"
        data-testid={`rec-audit-toggle-${index}`}
      >
        <Shield className="w-3 h-3" />
        {open ? 'Hide' : 'Why this recommendation?'}
        <Badge variant="outline" className="text-[8px] ml-1 px-1 py-0">SUPER ADMIN</Badge>
      </button>
      {open && (
        <div className="mt-2 p-2 rounded bg-black/5 dark:bg-white/5 text-[10px] space-y-2" data-testid={`rec-audit-body-${index}`}>
          <p className="opacity-70">
            Data the AI was given when generating <span className="font-mono">{rec.title?.slice(0, 40)}</span>:
          </p>
          {scalarEntries.length > 0 && (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 font-mono">
              {scalarEntries.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2 truncate">
                  <span className="opacity-60 truncate">{k}</span>
                  <span className="text-right truncate">{fmt(v)}</span>
                </div>
              ))}
            </div>
          )}
          {textEntries.map(([k, v]) => (
            <div key={k} className="space-y-1" data-testid={`rec-audit-block-${k}`}>
              <div className="text-[9px] uppercase tracking-wide opacity-60 font-semibold">{prettyLabel(k)}</div>
              <pre className="whitespace-pre-wrap font-mono text-[10px] leading-snug p-2 rounded bg-background/60 border border-current/10 overflow-x-auto max-h-72">
                {String(v).trim()}
              </pre>
            </div>
          ))}
          <div className="flex justify-between text-[9px] opacity-60 pt-1 border-t border-current/10">
            <span>cache: {audit.cache}</span>
            {audit.generated_at && <span>{new Date(audit.generated_at).toLocaleString()}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
